import { useCallback, useEffect, useState } from 'react';
import {
  ArrowBackRounded,
  CloseRounded,
  FolderOpenRounded,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { Box, CircularProgress } from '@mui/material';

import {
  Breadcrumb,
  Folder,
  getFoldersStructureByFolderIdRequest,
} from '../../../../services/requests';

import {
  BreadCrumbBox,
  BreadCrumbContainer,
  CardSectionBack,
  SectionInfo,
  HeaderDrawer,
  Section,
  SectionCard,
  SectionText,
  EmptySection,
  MoveButton,
} from './styles';

import useAuthStore from '../../../../store/auth';
import useArquiveStore from '../../../../store/arquives';
import useCompanyStore from '../../../../store/companies';
import useStatusButtonArchives from '../../../../store/statusButtonArchives';

dayjs().format();

interface MovingFileProps {
  files: Folder[] | undefined;
  setOpenDrawerMoving: () => void;
  folderId: string;
  handleMove: (folderId: string) => void;
  moveItemSelected: any;
}

export const MovingFile = ({
  files,
  setOpenDrawerMoving,
  folderId,
  handleMove,
  moveItemSelected,
}: MovingFileProps) => {
  const user = useAuthStore(state => state.user);
  const breadcrumbsStore = useArquiveStore(state => state.breadcrumbs);
  const statusButton = useStatusButtonArchives(
    state => state.statusButtonArchives,
  );
  const selectedCompany = useCompanyStore(state => state.selectedCompany);

  const [listagemPastas, setListagemPastas] = useState<any[]>([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [loadingFoldersStructure, setLoadingFoldersStructure] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);

  useEffect(() => {
    if (breadcrumbsStore && breadcrumbsStore?.length > 0) {
      setBreadcrumbs(breadcrumbsStore);
    }
  }, [breadcrumbsStore]);

  useEffect(() => {
    if (folderId === 'null') {
      const local = statusButton === 'gerais' ? 'manual' : 'automatic';
      setSelectedFolder(local);
    } else {
      setSelectedFolder(folderId);
    }
  }, [folderId, statusButton]);

  useEffect(() => {
    if (files && files?.length > 0) {
      const folders = files.filter((folder: any) => folder?.name !== undefined);

      const foldersFiltered = folders.filter((folder: any) => {
        const guestsIsEmpty = folder?.guests?.length === 0;
        const guestsIdsIncludesUser = folder?.guests_ids?.includes(user?.id);
        const ownerIsUser = folder?.owner_id === user?.id;

        return guestsIsEmpty || guestsIdsIncludesUser || ownerIsUser;
      });

      setListagemPastas(foldersFiltered);
    }
  }, [files, user?.id, breadcrumbs]);

  const handleBreadcrumbNavigation = useCallback(
    async (local: string) => {
      setSelectedFolder(local);

      setLoadingFoldersStructure(true);

      try {
        const response = await getFoldersStructureByFolderIdRequest(
          user?.id as string,
          selectedCompany?.id as string,
          local,
        );

        if (response?.status === 200) {
          const { data } = response;

          setListagemPastas(data);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingFoldersStructure(false);
      }
    },
    [user?.id, selectedCompany?.id],
  );

  const handleBack = useCallback(async () => {
    if (breadcrumbs.length > 1) {
      setLoadingFoldersStructure(true);

      const breadcrumbFiltered = breadcrumbs.slice(0, -1);

      setBreadcrumbs([...breadcrumbFiltered]);

      const lastBreadcrumb = breadcrumbFiltered[breadcrumbFiltered.length - 1];

      let local = '';

      if (lastBreadcrumb?.id === null) {
        local = statusButton === 'gerais' ? 'manual' : 'automatic';
      } else {
        local = lastBreadcrumb?.id;
      }

      await handleBreadcrumbNavigation(local);
    }
  }, [breadcrumbs, handleBreadcrumbNavigation, statusButton]);

  const handleFolderNavigate = useCallback(
    async (folder: Folder) => {
      const newBreadcrumb = {
        name: folder?.name,
        route: `/folder/${folder?.id}`,
        id: folder?.id,
      };

      setBreadcrumbs([...breadcrumbs, { ...newBreadcrumb }]);

      await handleBreadcrumbNavigation(folder?.id);
    },
    [breadcrumbs, handleBreadcrumbNavigation],
  );

  return (
    <Box width="480px" padding="20px">
      <HeaderDrawer>
        <CloseRounded
          sx={{ cursor: 'pointer', position: 'absolute', left: '0' }}
          onClick={setOpenDrawerMoving}
        />
        <span className="title">Mover pasta</span>
      </HeaderDrawer>
      <BreadCrumbContainer>
        {breadcrumbs.map((b: any, index: number) => (
          <BreadCrumbBox key={b.id}>
            <strong
              style={{
                marginRight: '8px',
                color: index === breadcrumbs.length - 1 ? '#ccc' : '#000',
              }}
            >{`${b.name} `}</strong>
            <strong>
              {b === breadcrumbs[breadcrumbs.length - 1] ? '' : '»'}
            </strong>
          </BreadCrumbBox>
        ))}
      </BreadCrumbContainer>
      {breadcrumbs.length > 1 && (
        <CardSectionBack
          variant="text"
          onClick={handleBack}
          disabled={loadingFoldersStructure}
        >
          <ArrowBackRounded />
          <SectionText>Pasta anterior</SectionText>
        </CardSectionBack>
      )}
      {loadingFoldersStructure ? (
        <Section>
          <EmptySection>
            <CircularProgress />
          </EmptySection>
        </Section>
      ) : (
        <Section>
          {listagemPastas?.length > 0 ? (
            <>
              {listagemPastas
                ?.filter((f: any) => f.id !== moveItemSelected.id)
                .map(pasta => (
                  <SectionCard
                    key={pasta?.id}
                    variant="outlined"
                    onClick={() => handleFolderNavigate(pasta)}
                  >
                    <SectionInfo>
                      <FolderOpenRounded
                        sx={{
                          color: '#000000;',
                        }}
                      />
                      <SectionText>{pasta?.name}</SectionText>
                    </SectionInfo>
                  </SectionCard>
                ))}
            </>
          ) : (
            <EmptySection>
              <SectionText>Não há mais pastas</SectionText>
            </EmptySection>
          )}

          <Box width="100%" display="flex" justifyContent="center">
            <MoveButton
              type="button"
              variant="contained"
              color="success"
              onClick={() => handleMove(selectedFolder)}
              disabled={selectedFolder === folderId}
            >
              Mover para esta pasta
            </MoveButton>
          </Box>
        </Section>
      )}
    </Box>
  );
};
